import React, { useState, useEffect, useRef } from 'react';
import { DndContext } from '@dnd-kit/core';
import MonthlyCalendar from './planning/MonthlyCalendar';
import TimelineGantt from './planning/TimelineGantt';
import ServicesAndBranchesPanel from './planning/ServicesAndBranchesPanel';
import FilterPanel from './planning/FilterPanel';
import TaskDetailsPanel from './planning/TaskDetailsPanel';
import NewPlanningItemModal from './planning/NewPlanningItemModal';
import { useGetPlanningData, useUpdateTaskSchedule, useUpdatePlanningItem, useAssignResource, useCreatePlanningItem } from '../hooks/usePlanning';
import { useToast } from './common/ToastProvider';
import api from '../api';
import { generatePlanningPdf } from '../utils/generatePlanningPdf';
import { useIsMobile } from '../hooks/useIsMobile';

function PlanningPage({ user }) {
  const { showToast } = useToast();
  
  // Ref for capturing Gantt chart
  const ganttRef = useRef(null);
  
  // State
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showServicesPanel, setShowServicesPanel] = useState(true);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'gantt'
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isMobile = useIsMobile();
  
  // Default to today and 3 months ahead
  const today = new Date();
  const threeMonthsAhead = new Date();
  threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3);
  
  const [filters, setFilters] = useState({
    view: 'single', // Default to single company view
    companyId: null, // User must select a company
    branchId: null,
    startDate: today.toISOString().split('T')[0],
    endDate: threeMonthsAhead.toISOString().split('T')[0],
  });
  const [companies, setCompanies] = useState([]);

  // Queries and mutations
  const { data: tasks = [], isLoading, error, refetch } = useGetPlanningData(filters);
  const updateTaskSchedule = useUpdateTaskSchedule();
  const updatePlanningItem = useUpdatePlanningItem();
  const assignResource = useAssignResource();
  const createPlanningItem = useCreatePlanningItem();

  // Fetch companies for filter
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const response = await api.get('/companies');
        setCompanies(response.data);
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    }
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowFilters(false);
      setShowServicesPanel(false);
    }
  }, [isMobile]);

  // When company is selected, fetch its details and update date range
  useEffect(() => {
    async function fetchCompanyDetails() {
      if (!filters.companyId) return;

      try {
        const response = await api.get(`/companies/${filters.companyId}`);
        const company = response.data;

        // Check if company has onboarding dates
        if (company.startDate && company.endDate) {
          // Set date range to company's onboarding period
          setFilters(prev => ({
            ...prev,
            startDate: new Date(company.startDate).toISOString().split('T')[0],
            endDate: new Date(company.endDate).toISOString().split('T')[0],
          }));
        }
      } catch (err) {
        console.error('Error fetching company details:', err);
      }
    }
    fetchCompanyDetails();
  }, [filters.companyId]);

  // Handle task click
  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  // Keep the selected task in sync after refetches so details match the bar
  useEffect(() => {
    if (!selectedTask) return;
    const updated = tasks.find(
      (t) => t.itemType === selectedTask.itemType && t.itemId === selectedTask.itemId
    );
    if (updated) {
      setSelectedTask(updated);
    }
  }, [tasks, selectedTask]);

  // Handle task date change (drag on timeline)
  const handleTaskDateChange = async (task, newStart, newEnd) => {
    console.log('handleTaskDateChange called:', { task, newStart, newEnd });
    
    try {
      const data = {
        startDate: newStart,
        endDate: newEnd,
      };

      console.log('Updating task with data:', data);

      if (task.itemType === 'planning') {
        console.log('Updating planning item ID:', task.itemId);
        await updatePlanningItem.mutateAsync({
          id: task.itemId,
          data,
        });
      } else if (task.itemType === 'task') {
        console.log('Updating task ID:', task.itemId);
        await updateTaskSchedule.mutateAsync({
          id: task.itemId,
          data,
        });
      }

      showToast('Task schedule updated successfully', 'success');
      
      // Force refetch to ensure UI updates
      refetch();
    } catch (error) {
      console.error('Error updating task schedule:', error);
      showToast('Failed to update task schedule', 'error');
    }
  };

  // Handle assigning/unassigning a resource (user) to a task
  // Supports toggling individual users for multi-assignment
  const handleAssignResource = async (task, user, isAssigned) => {
    try {
      // Get current assigned users
      const currentIds = task.assignedUserIds || 
        (task.assignedUserId ? [task.assignedUserId] : []);
      
      let newIds;
      if (isAssigned) {
        // Remove user from assignments
        newIds = currentIds.filter(id => id !== user.id);
      } else {
        // Add user to assignments (avoid duplicates)
        newIds = currentIds.includes(user.id) ? currentIds : [...currentIds, user.id];
      }
      
      await assignResource.mutateAsync({
        itemType: task.itemType,
        itemId: task.itemId,
        userIds: newIds,
      });
      
      const action = isAssigned ? 'removed from' : 'assigned to';
      showToast(`${user.name} ${action} ${task.name || task.title}`, 'success');
      refetch();
    } catch (error) {
      console.error('Error assigning resource:', error);
      showToast('Failed to update assignment', 'error');
    }
  };

  // Handle double click to create new item
  const handleCreateNewItem = () => {
    setShowNewItemModal(true);
  };

  // Handle drag and drop (resource assignment)
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    // Check if dragging a user onto a task
    if (active.id.startsWith('user-') && over.id.startsWith('task-')) {
      const userId = parseInt(active.id.replace('user-', ''));
      const taskId = over.id;

      // Find the task
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      try {
        await assignResource.mutateAsync({
          itemType: task.itemType,
          itemId: task.itemId,
          userId,
        });

        showToast('Resource assigned successfully', 'success');
      } catch (error) {
        console.error('Error assigning resource:', error);
        showToast('Failed to assign resource', 'error');
      }
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  // Handle task update from details panel
  const handleTaskUpdate = async (updatedData) => {
    if (!selectedTask) return;

    try {
      if (selectedTask.itemType === 'planning') {
        await updatePlanningItem.mutateAsync({
          id: selectedTask.itemId,
          data: updatedData,
        });
      } else if (selectedTask.itemType === 'task') {
        await updateTaskSchedule.mutateAsync({
          id: selectedTask.itemId,
          data: updatedData,
        });
      }

      showToast('Task updated successfully', 'success');
      setSelectedTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
      showToast('Failed to update task', 'error');
    }
  };

  // Handle drop of services/branches onto timeline
  const handleItemDrop = async (droppedItem, startDate) => {
    console.log('handleItemDrop called with:', droppedItem, startDate);
    
    if (!droppedItem || !startDate) {
      console.warn('Invalid drop - missing droppedItem or startDate');
      return;
    }

    // Validate company is selected
    if (!filters.companyId) {
      showToast('Please select a company first', 'error');
      return;
    }

    try {
      // Ensure we have a valid date string (YYYY-MM-DD format)
      const start = new Date(startDate);
      console.log('Start date:', start, 'Day of week:', start.getDay());
      
      // DON'T automatically move from weekends - let user place tasks where they want
      // User can manually adjust if needed
      
      // Calculate end date (default to 7 days for services)
      const end = new Date(start);
      end.setDate(end.getDate() + 7);

      const itemData = {
        title: droppedItem.itemType === 'service' 
          ? droppedItem.name 
          : `${droppedItem.name} - Onboarding`,
        description: droppedItem.itemType === 'service'
          ? `${droppedItem.name} setup and configuration`
          : `Complete onboarding for ${droppedItem.name}`,
        companyId: filters.companyId, // Use the selected company from filters
        branchId: droppedItem.itemType === 'branch' ? droppedItem.id : null,
        type: droppedItem.itemType === 'service' ? 'Service' : 'Branch Setup',
        priority: 'High',
        status: 'Planned',
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
        color: droppedItem.itemType === 'service' ? '#3B82F6' : '#8B5CF6',
      };

      console.log('Creating planning item:', itemData);
      await createPlanningItem.mutateAsync(itemData);
      showToast(`${droppedItem.name} added to timeline`, 'success');
    } catch (error) {
      console.error('Error creating planning item:', error);
      showToast('Failed to add item to timeline', 'error');
    }
  };

  // Handle PDF generation
  const handleGeneratePdf = async () => {
    if (tasks.length === 0) {
      showToast('No tasks to export. Please add some planning items first.', 'warning');
      return;
    }

    setIsGeneratingPdf(true);
    showToast('Generating PDF... Please wait.', 'info');

    try {
      // Get company name
      const selectedCompany = companies.find(c => c.id === filters.companyId);
      const companyName = selectedCompany?.name || 'Project Plan';

      // Calculate date range from tasks
      const taskDates = tasks.flatMap(t => [
        t.start ? new Date(t.start) : null,
        t.end ? new Date(t.end) : null,
      ]).filter(Boolean);

      const dateRange = {
        start: taskDates.length > 0 ? new Date(Math.min(...taskDates)) : new Date(),
        end: taskDates.length > 0 ? new Date(Math.max(...taskDates)) : new Date(),
      };

      // Capture the Gantt chart element (if in Gantt view)
      const ganttElement = viewMode === 'gantt' ? ganttRef.current : null;

      // Generate PDF
      const filename = await generatePlanningPdf(companyName, tasks, dateRange, ganttElement);
      
      showToast(`PDF generated successfully: ${filename}`, 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Failed to generate PDF. Please try again.', 'error');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      {/* Fullscreen Planning View with scroll support */}
      <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-auto">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
          <div className="px-4 sm:px-6 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-xl font-bold flex items-center gap-2">
                <span>📅</span>
                <span>Planning</span>
              </h1>
              <div className="flex flex-wrap gap-2">
                {/* View Toggle */}
                <div className="flex bg-white/10 rounded-md overflow-hidden">
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-3 py-1 text-sm transition-colors ${
                      viewMode === 'calendar' 
                        ? 'bg-white/30 font-semibold' 
                        : 'hover:bg-white/20'
                    }`}
                  >
                    📅 Calendar
                  </button>
                  <button
                    onClick={() => setViewMode('gantt')}
                    className={`px-3 py-1 text-sm transition-colors ${
                      viewMode === 'gantt' 
                        ? 'bg-white/30 font-semibold' 
                        : 'hover:bg-white/20'
                    }`}
                  >
                    📊 Gantt
                  </button>
                </div>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm transition-colors"
                >
                  {showFilters ? '🔍 Hide Filters' : '🔍 Show Filters'}
                </button>
                <button
                  onClick={() => setShowServicesPanel(!showServicesPanel)}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm transition-colors"
                >
                  {showServicesPanel ? '📦 Hide Services' : '📦 Show Services'}
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={handleGeneratePdf}
                disabled={isGeneratingPdf || tasks.length === 0}
                className={`px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium ${
                  isGeneratingPdf || tasks.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
                }`}
              >
                {isGeneratingPdf ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <span>📄</span>
                    <span>Generate Plan</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowNewItemModal(true)}
                className="px-4 py-2 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
              >
                <span>➕</span>
                <span>New Item</span>
              </button>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
              >
                <span>←</span>
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Horizontal flex container for all three columns - min-h-[600px] ensures scrolling on small windows */}
        <div className="flex-1 flex flex-col md:flex-row min-h-[600px]">
          {/* Left Panel - Filters */}
          {showFilters && (
            <div 
              className="w-full md:w-80 flex-shrink-0 bg-white dark:bg-gray-800 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 flex flex-col shadow-md"
            >
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between flex-shrink-0">
                <h3 className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  <span>🔍</span>
                  <span>Filters</span>
                </h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 transition-colors"
                >
                  ✕
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <FilterPanel
                  filters={filters}
                  companies={companies}
                  onFilterChange={handleFilterChange}
                />
              </div>
            </div>
          )}

          {/* Main Content - Chart */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Timeline/Calendar Content */}
            <div className="flex-1 relative">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading planning data...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="mx-auto h-16 w-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">Error loading planning data</h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
                  </div>
                </div>
              ) : (
                <>
                  {viewMode === 'calendar' ? (
                    <MonthlyCalendar
                      tasks={tasks}
                      onTaskClick={handleTaskClick}
                      onItemDrop={handleItemDrop}
                      onAssignResource={handleAssignResource}
                      companies={companies}
                    />
                  ) : (
<div ref={ganttRef}>
                                      <TimelineGantt
                                        tasks={tasks}
                                        onTaskClick={handleTaskClick}
                                        onTaskDateChange={handleTaskDateChange}
                                        onItemDrop={handleItemDrop}
                                        onAssignResource={handleAssignResource}
                                        companies={companies}
                                        companyId={filters.companyId}
                                      />
                                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Panel - Services or Task Details */}
          {(showServicesPanel || selectedTask) && (
            <div 
              className="w-full md:w-80 flex-shrink-0 bg-white dark:bg-gray-800 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 flex flex-col shadow-md"
            >
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between flex-shrink-0">
                <h3 className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  {selectedTask ? (
                    <>
                      <span>📋</span>
                      <span>Task Details</span>
                    </>
                  ) : (
                    <>
                      <span>📦</span>
                      <span>Services & Branches</span>
                    </>
                  )}
                </h3>
                <button
                  onClick={() => {
                    if (selectedTask) {
                      setSelectedTask(null);
                    } else {
                      setShowServicesPanel(false);
                    }
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 transition-colors"
                >
                  ✕
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                {selectedTask ? (
                  <TaskDetailsPanel
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onUpdate={handleTaskUpdate}
                  />
                ) : (
                  <ServicesAndBranchesPanel 
                    companyId={filters.companyId}
                    onDragStart={() => console.log('Drag started')}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* New Planning Item Modal */}
        {showNewItemModal && (
          <NewPlanningItemModal
            companies={companies}
            onClose={() => setShowNewItemModal(false)}
            onSuccess={() => {
              setShowNewItemModal(false);
              showToast('Planning item created successfully', 'success');
            }}
          />
        )}
      </div>
    </DndContext>
  );
}

export default PlanningPage;

